import { ControllerParams } from '@wix/yoshi-flow-editor';
import { uniqueId } from 'lodash';
import { useEffect, useRef } from 'react';

type CarryOriginal<T extends (...args: any[]) => any> = {
  // Phantom type to help inference of the original signature
  __repromiseOriginal__?: T;
};

export type WrappedWithRepromise<T extends (...args: any[]) => any> = ((
  execId: string,
  ...args: any[]
) => Promise<void>) &
  CarryOriginal<T>;

export const wrapWithRepromise = <T extends (...args: any[]) => any>(
  fn: T,
  setProps: ControllerParams['controllerConfig']['setProps'],
): WrappedWithRepromise<T> => {
  const wrapped = (async (execId: string, ...args: Parameters<T>) => {
    try {
      const results = await fn(...args);
      setProps({
        executionResults: {
          [execId]: {
            status: 'success',
            results,
          },
        },
      });
    } catch (error) {
      setProps({
        executionResults: {
          [execId]: {
            status: 'error',
            error,
          },
        },
      });
    }
  }) as WrappedWithRepromise<T>;

  return wrapped;
};

export const useRepromise = <Original extends (...args: any[]) => any>(
  fn: WrappedWithRepromise<Original>,
  props: {
    // Help us figure out the type of this structure if it's there, without hurting the callability
    // of this function by demanding it's caller to know what props we've set and
    executionResults?: {
      [execId: string]:
        | { status: 'success'; results: Awaited<ReturnType<Original>> }
        | { status: 'error'; error: unknown };
    };
  } & Record<string, unknown>,
): ((
  ...args: Parameters<Original>
) => Promise<Awaited<ReturnType<Original>>>) => {
  const listenersRef = useRef<{
    [executionId: string]: [Function, Function];
  }>({});

  useEffect(() => {
    Object.entries(props.executionResults ?? {}).forEach(
      ([execId, execution]) => {
        const handlers = listenersRef.current[execId];
        if (!handlers) {
          return;
        }

        const [resolve, reject] = handlers;
        if (execution.status === 'success') {
          resolve(execution.results);
        } else {
          reject(execution.error);
        }

        Reflect.deleteProperty(listenersRef.current, execId);
      },
    );
  }, [props.executionResults]);

  return (...args: Parameters<Original>) => {
    return new Promise<Awaited<ReturnType<Original>>>((resolve, reject) => {
      const execId = uniqueId(`${fn.name ?? 'anonymous'}_`);
      fn(execId, ...args);

      listenersRef.current[execId] = [resolve, reject];
    });
  };
};
